import { Logger } from '@nextgen-web-framework/core';
import { Category, categoryApi, type CategoryAttributes, type ResponseCategories } from '../services';
import { type BreadcrumbProps, type IProduct, ProjectionType } from '../types';
import { logObject } from '../utils';

const loggerInstance = Logger.getInstance(logObject('breadcrumbTransformer'));
const defaultLanguage = 'en-us';
const API_VERSION_V1 = 'v1';

/**
 * @param {IProduct}  productData - productData.
 * @param {string} language - A locale value, default value is 'en-us'
 * @returns {Promise<BreadcrumbProps>} - Returns a promise with breadcrumb data
 */
const PDPBreadcrumbTransformer = async (
  productData: IProduct,
  language = defaultLanguage,
  treeId?: string,
): Promise<BreadcrumbProps> => {
  try {
    const { defaultCategoryId = '', productName, productId } = productData;
    const productTitleBreadcrumbData = generateBreadcrumbProductTitleLink(productId, productName);
    const categoryData = await categoryApi.getCategoryById({
      categoryId: defaultCategoryId,
      treeId,
    });
    if (!('id' in categoryData)) {
      throw new Error('Error occured while fetching category details');
    }
    const categoryIds = categoryIdsTransformer(categoryData);
    const categoriesData = await categoryApi.getCategoriesByIds(categoryIds, API_VERSION_V1, ProjectionType.Cat);
    const breadcrumbData = categoryBreadcrumbTransformer(categoriesData, categoryIds, language, categoryData.treeId);
    breadcrumbData.breadcrumbData.push(productTitleBreadcrumbData);
    loggerInstance.log('PDP breadcrumb data generated successfully...');
    return breadcrumbData;
  } catch (e) {
    loggerInstance.error('Error occured while generating PDP breadcrumb data', e);
    throw e;
  }
};

/**
 * @param {Category}  category - category object.
 * @param {string} language - A locale value, default value is 'en-us'
 * @returns {Promise<BreadcrumbProps>} - Returns a promise with breadcrumb data
 */
const PLPBreadcrumbTransformer = async (category: Category, language = defaultLanguage): Promise<BreadcrumbProps> => {
  try {
    const { treeId } = category;
    const categoryIds = categoryIdsTransformer(category);
    const categoriesData = await categoryApi.getCategoriesByIds(categoryIds, API_VERSION_V1, ProjectionType.Cat);
    const breadcrumbData = categoryBreadcrumbTransformer(categoriesData, categoryIds, language, treeId);
    loggerInstance.log('PLP breadcrumb data generated successfully...');
    return breadcrumbData;
  } catch (e) {
    loggerInstance.error('Error occured while generating PDP breadcrumb data', e);
    throw e;
  }
};

const generateBreadcrumbProductTitleLink = (id: string, productName: string) => ({
  link: `product/${id}`,
  name: productName,
});

const categoryIdsTransformer = ({ path }: CategoryAttributes): string[] => {
  if (path) {
    return path.split('/').filter((route: string) => route);
  }
  return [];
};

const categoryBreadcrumbTransformer = (
  { categories }: ResponseCategories,
  catIds: string[],
  langauge = defaultLanguage,
  treeId: string,
): BreadcrumbProps => {
  if (categories && categories.length > 0) {
    return {
      breadcrumbData: catIds.map((catId) => {
        const { id = '', attribs = {} } = categories.find((category) => category.id === catId) || {};
        const categoryText = treeId === 'CAT' ? 'category' : 'solution';
        return {
          link: `/${categoryText}/${id}`,
          name: attribs?.[langauge]?.name || '',
        };
      }),
    };
  }
  return { breadcrumbData: [] };
};

export { categoryBreadcrumbTransformer, categoryIdsTransformer, PDPBreadcrumbTransformer, PLPBreadcrumbTransformer };
export type { BreadcrumbProps };
